import { parseAsInteger, useQueryState } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search } from 'lucide-react'
import { useContacts } from '@/api/hooks'
import { parseUTCDate, resultCount } from '@/lib/utils'
import { SearchInput } from '@/components/search-input'
import { ResultCount } from '@/components/list-filters'

export function ContactsPage() {
  const [search, setSearch] = useUrlSearchText()
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))

  const { data, isLoading } = useContacts({
    search: search || undefined,
    page,
    limit: 20,
    sort_by: 'email',
    sort_dir: 'asc',
  })

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-56 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              void setPage(1)
            }}
            className="pl-9"
          />
        </div>
        <ResultCount>{resultCount(data?.total, 'contact')}</ResultCount>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : data?.items?.length ? (
              data.items.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    <Link
                      to="/contacts/$contactId"
                      params={{ contactId: String(contact.id) }}
                      className="font-medium text-primary hover:underline"
                    >
                      {contact.name || contact.email}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{contact.email}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {parseUTCDate(contact.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  No contacts found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : data?.items?.length ? (
          data.items.map((contact) => (
            <Link
              key={contact.id}
              to="/contacts/$contactId"
              params={{ contactId: String(contact.id) }}
              className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <p className="break-words text-sm font-medium text-primary">
                {contact.name || contact.email}
              </p>
              {contact.name && (
                <p className="mt-0.5 break-all text-xs text-muted-foreground">{contact.email}</p>
              )}
              <p className="mt-0.5 text-xs text-muted-foreground">
                Created {parseUTCDate(contact.created_at).toLocaleDateString()}
              </p>
            </Link>
          ))
        ) : (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            No contacts found
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setPage((p) => p + 1)}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
